import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Loader2, Flame, Eye, Clock, Heart, TrendingUp, Shield, AlertTriangle, LogIn, Lock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import MonthlyBaziPillars from '../components/MonthlyBaziPillars';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Heavenly Stems and their elements
const STEMS = ['甲', '乙', '丙', '丁', '戊', '己', '庚', '辛', '壬', '癸'];
const STEM_ELEMENTS = ['Wood', 'Wood', 'Fire', 'Fire', 'Earth', 'Earth', 'Metal', 'Metal', 'Water', 'Water'];

// Earthly Branches and their animals
const BRANCHES = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'];
const BRANCH_ANIMALS = ['Rat', 'Ox', 'Tiger', 'Rabbit', 'Dragon', 'Snake', 'Horse', 'Goat', 'Monkey', 'Rooster', 'Dog', 'Pig'];

// Month names
const MONTHS = ['Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'];

// Calculate Four Quadrants based on BaZi interaction with 2026 Fire Horse
// Uses backend BaZi data for accurate calculations
function calculateQuadrants(baziData) {
    // Get day master element from backend data
    const dayMasterElement = baziData.day_master?.element || 'fire';
    const dayMasterChinese = baziData.day_master?.chinese || '丙';
    const dayMasterEnglish = baziData.day_master?.english || 'Fire Yang';
    
    // Get pillar indices from backend data
    const dayBranchIndex = baziData.pillars?.day?.branch_index ?? 0;
    const hourStemIndex = baziData.pillars?.hour?.stem_index ?? 0;
    const hourBranchIndex = baziData.pillars?.hour?.branch_index ?? 0;
    const monthStemIndex = baziData.pillars?.month?.stem_index ?? 0;
    const monthBranchIndex = baziData.pillars?.month?.branch_index ?? 0;
    const yearStemIndex = baziData.pillars?.year?.stem_index ?? 0;
    const yearBranchIndex = baziData.pillars?.year?.branch_index ?? 0;
    
    // Capitalize element for lookup
    const elementCapitalized = dayMasterElement.charAt(0).toUpperCase() + dayMasterElement.slice(1);
    
    const elementInteraction = {
        Wood: { Fire: 85, Earth: 60, Metal: 45, Water: 70, Wood: 65 },
        Fire: { Fire: 70, Earth: 80, Metal: 55, Water: 35, Wood: 85 },
        Earth: { Fire: 75, Earth: 65, Metal: 80, Water: 50, Wood: 45 },
        Metal: { Fire: 40, Earth: 75, Metal: 65, Water: 80, Wood: 55 },
        Water: { Fire: 35, Earth: 55, Metal: 75, Water: 65, Wood: 80 }
    };
    
    const baseScore = elementInteraction[elementCapitalized]?.['Fire'] || 70;
    
    // Major Yang (Direct Wealth)
    let majorYang = baseScore;
    const harmonyBranches = [2, 10]; // Tiger, Dog - harmony with Horse
    if (harmonyBranches.includes(dayBranchIndex)) majorYang += 15;
    if (dayBranchIndex === 6) majorYang -= 20; // Horse - self-penalty
    majorYang = Math.min(95, Math.max(25, majorYang));
    
    // Minor Yang (Intuitive Intelligence)
    const hourElement = STEM_ELEMENTS[hourStemIndex] || 'Fire';
    let minorYang = elementInteraction[hourElement]?.['Fire'] || 70;
    if ([2, 6, 10].includes(hourBranchIndex)) minorYang += 10;
    minorYang = Math.min(95, Math.max(25, minorYang));
    
    // Major Yin (Timing & Alignment)
    const monthElement = STEM_ELEMENTS[monthStemIndex] || 'Earth';
    let majorYin = elementInteraction[monthElement]?.['Fire'] || 70;
    const clashBranch = (monthBranchIndex + 6) % 12;
    if (clashBranch === 6) majorYin -= 15;
    if ([2, 10].includes(monthBranchIndex)) majorYin += 12;
    majorYin = Math.min(95, Math.max(25, majorYin));
    
    // Minor Yin (Emotional/Relational)
    const yearElement = STEM_ELEMENTS[yearStemIndex] || 'Earth';
    let minorYin = elementInteraction[yearElement]?.['Fire'] || 70;
    if (yearBranchIndex === 6) minorYin += 10; // Born in Horse year
    if (yearBranchIndex === 0) minorYin -= 15; // Rat clashes Horse
    minorYin = Math.min(95, Math.max(25, minorYin));
    
    return {
        majorYang: Math.round(majorYang),
        minorYang: Math.round(minorYang),
        majorYin: Math.round(majorYin),
        minorYin: Math.round(minorYin),
        dayMaster: dayMasterChinese,
        dayMasterElement: elementCapitalized,
        dayMasterEnglish: dayMasterEnglish,
        overallScore: Math.round((majorYang + minorYang + majorYin + minorYin) / 4)
    };
}

// Calculate monthly energy based on BaZi
function calculateMonthlyEnergy(baziData, quadrants) {
    const baseEnergy = quadrants.majorYang;
    const dayBranch = baziData.pillars?.day?.branch_index ?? 0;
    const yearBranch = baziData.pillars?.year?.branch_index ?? 0;
    
    return MONTHS.map((month, i) => {
        // Month branch for 2026 (Feb = Tiger = 2, etc.)
        const monthBranch = (i + 2) % 12;
        
        // Calculate positive factors
        let positiveEnergy = baseEnergy;
        
        // Harmony bonus
        if ((dayBranch + monthBranch) % 6 === 0) positiveEnergy += 15;
        
        // Tiger-Horse-Dog harmony (Fire frame)
        if ([2, 6, 10].includes(monthBranch)) positiveEnergy += 8;
        
        // Day branch harmony with month
        const harmonyCombos = { 0: 1, 2: 11, 3: 10, 4: 9, 5: 8, 6: 7 };
        if (harmonyCombos[dayBranch] === monthBranch || harmonyCombos[monthBranch] === dayBranch) {
            positiveEnergy += 10;
        }
        
        // Seasonal variation (positive)
        const seasonalBoost = Math.max(0, Math.sin((i + dayBranch) * 0.5) * 12);
        positiveEnergy += seasonalBoost;
        
        // Calculate challenge factors (for 2026 Fire Horse year)
        let challengeEnergy = 15; // Base challenge in Fire Horse year
        
        // Clash penalty - major challenge
        if ((dayBranch + 6) % 12 === monthBranch) challengeEnergy += 25;
        
        // Self-penalty for Horse months
        if (monthBranch === 6) challengeEnergy += 15; // Horse month self-clash
        
        // Fire-Fire dominance challenge in Fire months
        if ([5, 6].includes(monthBranch)) challengeEnergy += 10; // Snake, Horse months
        
        // Rat month clash with Horse year
        if (monthBranch === 0) challengeEnergy += 12;
        
        // Year branch clash with month
        if ((yearBranch + 6) % 12 === monthBranch) challengeEnergy += 8;
        
        // Negative seasonal variation
        const seasonalDrop = Math.max(0, -Math.sin((i + dayBranch) * 0.5) * 10);
        challengeEnergy += seasonalDrop;
        
        // Normalize values
        positiveEnergy = Math.min(85, Math.max(30, Math.round(positiveEnergy)));
        challengeEnergy = Math.min(50, Math.max(10, Math.round(challengeEnergy)));
        
        // Net energy for display
        const netEnergy = Math.max(30, positiveEnergy - challengeEnergy);
        
        return {
            positive: positiveEnergy,
            challenge: challengeEnergy,
            net: netEnergy
        };
    });
}

// Quadrant Card Component
function QuadrantCard({ title, titleChinese, subtitle, icon: Icon, value, color }) {
    const colorMap = {
        red: { gradient: 'from-red-500 to-red-400', text: 'text-red-600', bar: 'bg-red-500' },
        amber: { gradient: 'from-amber-500 to-amber-400', text: 'text-amber-600', bar: 'bg-amber-500' },
        blue: { gradient: 'from-blue-500 to-blue-400', text: 'text-blue-600', bar: 'bg-blue-500' },
        purple: { gradient: 'from-purple-500 to-purple-400', text: 'text-purple-600', bar: 'bg-purple-500' }
    };
    const c = colorMap[color];
    
    return (
        <Card className="bg-white">
            <CardContent className="pt-5">
                <div className="flex items-center gap-2 mb-1">
                    <Icon className={`w-4 h-4 ${c.text}`} />
                    <span className="font-semibold text-slate-800">{title}</span>
                    <span className="text-slate-500">{titleChinese}</span>
                </div>
                <p className="text-xs text-muted-foreground mb-3">{subtitle}</p>
                <div className="flex items-end gap-3">
                    <span className={`text-4xl font-bold ${c.text}`}>{value}%</span>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden mb-2">
                        <div className={`h-full ${c.bar} transition-all duration-1000`} style={{ width: `${value}%` }} />
                    </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                    {subtitle === 'Direct Wealth Capacity' && 'Your ability to convert activities into tangible financial outcomes and career advancement.'}
                    {subtitle === 'Intuitive Intelligence' && 'Capacity to read people, foresee risks, and respond strategically to subtle cues.'}
                    {subtitle === 'Timing & Alignment' && 'When opportunities and resources synchronize for optimal results.'}
                    {subtitle === 'Emotional Dynamics' && 'Managing relationships and emotional intelligence for success.'}
                </p>
            </CardContent>
        </Card>
    );
}

// Monthly Energy Bar Component - Stacked Green (Positive) and Red (Challenge)
function MonthlyEnergyBar({ month, energyData }) {
    // Handle both old format (number) and new format (object)
    const positive = typeof energyData === 'object' ? energyData.positive : energyData;
    const challenge = typeof energyData === 'object' ? energyData.challenge : 15;
    const total = positive + challenge;
    
    // Calculate percentages for stacked display
    const positiveHeight = (positive / total) * 100;
    const challengeHeight = (challenge / total) * 100;
    
    return (
        <div className="text-center">
            <div className="text-xs font-medium text-slate-600 mb-1">{month}</div>
            <div className="h-24 bg-slate-100 rounded-lg relative overflow-hidden flex flex-col justify-end">
                {/* Red (Challenge) portion - on top */}
                <div 
                    className="w-full bg-red-400 transition-all duration-700"
                    style={{ height: `${challengeHeight}%` }}
                />
                {/* Green (Positive) portion - on bottom */}
                <div 
                    className="w-full bg-emerald-400 transition-all duration-700"
                    style={{ height: `${positiveHeight}%` }}
                />
            </div>
            <div className="flex justify-center gap-1 mt-1">
                <span className="text-xs font-semibold text-emerald-600">{positive}</span>
                <span className="text-xs text-slate-400">/</span>
                <span className="text-xs font-semibold text-red-500">{challenge}</span>
            </div>
        </div>
    );
}

// Main Component
function CrimsonHorse() {
    const { user, token, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [baziData, setBaziData] = useState(null);
    const [quadrants, setQuadrants] = useState(null);
    const [monthlyEnergy, setMonthlyEnergy] = useState([]);
    const [error, setError] = useState(null);
    const [hasProAccess, setHasProAccess] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [monthlyBaziPillars, setMonthlyBaziPillars] = useState(null);
    const [loadingBaziPillars, setLoadingBaziPillars] = useState(false);
    const currentMonth = new Date().getMonth() + 1;
    
    // Check if user has PRO access
    useEffect(() => {
        const checkProAccess = async () => {
            if (!token) {
                setHasProAccess(false);
                setIsAdmin(false);
                return;
            }
            try {
                const response = await fetch(`${API_URL}/api/auth/admin/check`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    setHasProAccess(data.has_pro_access);
                    setIsAdmin(data.is_admin || false);
                }
            } catch (error) {
                console.error('Pro access check error:', error);
                setHasProAccess(false);
                setIsAdmin(false);
            }
        };
        checkProAccess();
    }, [token]);
    
    // Fetch monthly BaZi pillars for PRO users
    useEffect(() => {
        const fetchMonthlyBaziPillars = async () => {
            if (!hasProAccess) return;
            setLoadingBaziPillars(true);
            try {
                var url = API_URL + '/api/tong-shu/monthly-bazi/2026';
                if (user && user.birth_year && user.birth_month && user.birth_day) {
                    url += '?birth_year=' + user.birth_year + '&birth_month=' + user.birth_month + '&birth_day=' + user.birth_day;
                    if (user.birth_hour !== undefined && user.birth_hour !== null) {
                        url += '&birth_hour=' + user.birth_hour;
                    }
                }
                var response = await fetch(url);
                if (response.ok) {
                    var data = await response.json();
                    setMonthlyBaziPillars(data);
                }
            } catch (err) {
                console.error('Failed to fetch monthly BaZi pillars:', err);
            } finally {
                setLoadingBaziPillars(false);
            }
        };
        fetchMonthlyBaziPillars();
    }, [hasProAccess, user]);
    
    // Derive computed values directly from user data
    const hasCompleteBirthData = !!(isAuthenticated && user && user.birth_year && user.birth_month && user.birth_day);
    
    // Fetch BaZi from backend API when user has complete birth data
    useEffect(() => {
        const fetchBaZi = async () => {
            if (!hasCompleteBirthData || !token) {
                setLoading(false);
                return;
            }
            
            try {
                setLoading(true);
                setError(null);
                
                const hour = user.time_unknown ? 12 : (user.birth_hour || 12);
                
                const response = await fetch(`${API_URL}/api/tong-shu/personal-bazi`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        birth_year: user.birth_year,
                        birth_month: user.birth_month,
                        birth_day: user.birth_day,
                        birth_hour: hour
                    })
                });
                
                if (!response.ok) {
                    throw new Error('Failed to fetch BaZi data');
                }
                
                const data = await response.json();
                setBaziData(data);
                
                // Calculate quadrants and monthly energy using backend data
                const calculatedQuadrants = calculateQuadrants(data);
                const calculatedMonthly = calculateMonthlyEnergy(data, calculatedQuadrants);
                
                setQuadrants(calculatedQuadrants);
                setMonthlyEnergy(calculatedMonthly);
            } catch (err) {
                console.error('Error fetching BaZi:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        
        fetchBaZi();
    }, [hasCompleteBirthData, token, user]);

    // Not logged in state
    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-red-50 to-orange-50">
                <div className="bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 text-white py-12 px-4">
                    <div className="max-w-6xl mx-auto text-center">
                        <div className="flex items-center justify-center gap-3 mb-3">
                            <Flame className="w-8 h-8" />
                            <h1 className="text-3xl sm:text-4xl font-serif font-bold">Crimson Horse 2026</h1>
                            <Flame className="w-8 h-8" />
                        </div>
                        <p className="text-lg opacity-90">丙午 Fire Horse Year • Four Quadrants Analysis</p>
                    </div>
                </div>
                <div className="max-w-md mx-auto px-4 py-16">
                    <Card className="text-center">
                        <CardContent className="pt-8 pb-8">
                            <LogIn className="w-16 h-16 text-red-400 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold mb-2">Login Required</h3>
                            <p className="text-muted-foreground mb-6">
                                Please login to view your personalized 2026 Crimson Horse forecast based on your BaZi.
                            </p>
                            <Button 
                                onClick={() => navigate('/login')}
                                className="bg-gradient-to-r from-red-600 to-orange-500"
                            >
                                Login to Continue
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    // No birth data state
    if (!hasCompleteBirthData) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-red-50 to-orange-50">
                <div className="bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 text-white py-12 px-4">
                    <div className="max-w-6xl mx-auto text-center">
                        <div className="flex items-center justify-center gap-3 mb-3">
                            <Flame className="w-8 h-8" />
                            <h1 className="text-3xl sm:text-4xl font-serif font-bold">Crimson Horse 2026</h1>
                            <Flame className="w-8 h-8" />
                        </div>
                        <p className="text-lg opacity-90">丙午 Fire Horse Year • Four Quadrants Analysis</p>
                    </div>
                </div>
                <div className="max-w-md mx-auto px-4 py-16">
                    <Card className="text-center">
                        <CardContent className="pt-8 pb-8">
                            <AlertTriangle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold mb-2">Birth Data Required</h3>
                            <p className="text-muted-foreground mb-6">
                                Please update your profile with your complete date of birth to calculate your personalized 2026 forecast.
                            </p>
                            <Button 
                                onClick={() => navigate('/calendar')}
                                className="bg-gradient-to-r from-amber-500 to-orange-500"
                            >
                                Go to Dashboard Settings
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-red-50 to-orange-50 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-red-600" />
            </div>
        );
    }

    // Data not loaded yet - show loading
    if (!baziData || !quadrants) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-red-50 to-orange-50 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-red-600" />
            </div>
        );
    }

    // Get birth animal from user profile or BaZi data
    const yearBranchIndex = baziData.pillars?.year?.branch_index ?? ((user.birth_year - 4) % 12);
    const birthAnimal = BRANCH_ANIMALS[yearBranchIndex];
    const yearBranch = BRANCHES[yearBranchIndex];

    return (
        <div className="min-h-screen bg-gradient-to-b from-amber-50/50 to-orange-50/30" data-testid="crimson-horse-page">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 text-white py-8 px-4">
                <div className="max-w-6xl mx-auto text-center">
                    <div className="flex items-center justify-center gap-3 mb-2">
                        <Flame className="w-7 h-7" />
                        <h1 className="text-2xl sm:text-3xl font-serif font-bold">Crimson Horse 2026</h1>
                        <Flame className="w-7 h-7" />
                    </div>
                    <p className="text-sm opacity-90">丙午 Fire Horse Year • Four Quadrants Analysis</p>
                    <div className="mt-3 flex items-center justify-center gap-2 text-sm">
                        <Badge variant="secondary" className="bg-white/20 text-white">
                            {user.birth_year}/{user.birth_month}/{user.birth_day}
                        </Badge>
                        <Badge variant="secondary" className="bg-white/20 text-white">
                            {birthAnimal} ({yearBranch})
                        </Badge>
                        <Badge variant="secondary" className="bg-white/20 text-white">
                            Day Master: {quadrants.dayMaster} ({quadrants.dayMasterElement})
                        </Badge>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 py-6">
                {/* Four Quadrants */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <QuadrantCard 
                        title="Major Yang"
                        titleChinese="大陽"
                        subtitle="Direct Wealth Capacity"
                        icon={TrendingUp}
                        value={quadrants.majorYang}
                        color="red"
                    />
                    <QuadrantCard 
                        title="Minor Yang"
                        titleChinese="小陽"
                        subtitle="Intuitive Intelligence"
                        icon={Eye}
                        value={quadrants.minorYang}
                        color="amber"
                    />
                    <QuadrantCard 
                        title="Major Yin"
                        titleChinese="大陰"
                        subtitle="Timing & Alignment"
                        icon={Clock}
                        value={quadrants.majorYin}
                        color="blue"
                    />
                    <QuadrantCard 
                        title="Minor Yin"
                        titleChinese="小陰"
                        subtitle="Emotional Dynamics"
                        icon={Heart}
                        value={quadrants.minorYin}
                        color="purple"
                    />
                </div>

                {/* Challenges & Recommendations */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <Card className="bg-white">
                        <CardHeader className="pb-3">
                            <CardTitle className="flex items-center gap-2 text-rose-600 text-lg">
                                <AlertTriangle className="w-5 h-5" />
                                Key Challenges in 2026
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex gap-3">
                                <div className="w-1 bg-rose-400 rounded flex-shrink-0" />
                                <div>
                                    <p className="font-semibold text-sm text-slate-800">Fire-Fire Dominance</p>
                                    <p className="text-xs text-muted-foreground">Emotional volatility and impulsive decisions due to absent Water element.</p>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <div className="w-1 bg-rose-400 rounded flex-shrink-0" />
                                <div>
                                    <p className="font-semibold text-sm text-slate-800">Intuitive Blind Spots</p>
                                    <p className="text-xs text-muted-foreground">Misdirected efforts from assuming visibility without active positioning.</p>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <div className="w-1 bg-rose-400 rounded flex-shrink-0" />
                                <div>
                                    <p className="font-semibold text-sm text-slate-800">Erratic Priorities</p>
                                    <p className="text-xs text-muted-foreground">Workplace chaos from uncohesive teams and reversed decisions.</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-white">
                        <CardHeader className="pb-3">
                            <CardTitle className="flex items-center gap-2 text-emerald-600 text-lg">
                                <Shield className="w-5 h-5" />
                                Strategic Recommendations
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex gap-3">
                                <div className="w-1 bg-emerald-400 rounded flex-shrink-0" />
                                <div>
                                    <p className="font-semibold text-sm text-slate-800">Align Quadrants</p>
                                    <p className="text-xs text-muted-foreground">Correct misalignments to gain clarity, visibility, and stability.</p>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <div className="w-1 bg-emerald-400 rounded flex-shrink-0" />
                                <div>
                                    <p className="font-semibold text-sm text-slate-800">Build Protective Structures</p>
                                    <p className="text-xs text-muted-foreground">Focus on defensive positioning rather than aggressive expansion.</p>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <div className="w-1 bg-emerald-400 rounded flex-shrink-0" />
                                <div>
                                    <p className="font-semibold text-sm text-slate-800">Strategic Calm</p>
                                    <p className="text-xs text-muted-foreground">Intentional moves and reinvention over reactive overthinking.</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Monthly BaZi Pillars - PRO Only */}
                <MonthlyBaziPillars 
                    monthlyBaziPillars={monthlyBaziPillars}
                    loadingBaziPillars={loadingBaziPillars}
                    year={2026}
                    month={currentMonth}
                    setMonth={function() {}}
                    isPro={hasProAccess}
                    isAdmin={isAdmin}
                />
            </div>
        </div>
    );
}

export default CrimsonHorse;
